import type { ReactElement } from 'react';
import { useTheme } from 'next-themes';
import { ImageIcon, Play } from 'lucide-react';

interface ImagePlaceholderProps {
  label: string;
  aspectRatio?: '16/9' | '4/3' | '1/1';
  variant?: 'screenshot' | 'video';
  className?: string;
  /** Light mode image path (e.g., '/images/tour/create-page-light.png') */
  imageSrcLight?: string;
  /** Dark mode image path (e.g., '/images/tour/create-page-dark.png') */
  imageSrcDark?: string;
  /** How to fit the image - 'cover' crops to fill, 'contain' shows full image */
  objectFit?: 'cover' | 'contain';
}

const ImagePlaceholder = ({
  label,
  aspectRatio = '16/9',
  variant = 'screenshot',
  className = '',
  imageSrcLight,
  imageSrcDark,
  objectFit = 'cover'
}: ImagePlaceholderProps): ReactElement => {
  const { resolvedTheme } = useTheme();
  const Icon = variant === 'video' ? Play : ImageIcon;

  const aspectRatioClass = {
    '16/9': 'aspect-video',
    '4/3': 'aspect-[4/3]',
    '1/1': 'aspect-square'
  }[aspectRatio];

  // Determine which image to show based on theme
  const imageSrc = resolvedTheme === 'dark' ? imageSrcDark : imageSrcLight;
  const hasImage = imageSrc && imageSrc.length > 0;

  // If we have an image, render it
  if (hasImage) {
    return (
      <div
        className={`${aspectRatioClass} w-full rounded-xl overflow-hidden transition-all hover:scale-[1.01] ${className}`}
        style={{
          boxShadow: '0 8px 24px rgba(92, 64, 51, 0.12)',
          border: '1px solid var(--st-border-light)'
        }}
      >
        <img
          src={imageSrc}
          alt={label}
          className={`w-full h-full ${objectFit === 'contain' ? 'object-contain' : 'object-cover'}`}
        />
      </div>
    );
  }

  // Otherwise, render the placeholder
  return (
    <div
      className={`${aspectRatioClass} w-full rounded-xl flex flex-col items-center justify-center gap-3 transition-all hover:scale-[1.01] ${className}`}
      style={{
        background: 'linear-gradient(135deg, var(--st-bg-page) 0%, var(--st-border-light) 100%)',
        border: '2px dashed var(--st-border-medium)'
      }}
    >
      <div
        className="w-14 h-14 rounded-xl flex items-center justify-center"
        style={{ background: 'var(--st-bg-surface)' }}
      >
        <Icon
          className="w-7 h-7"
          style={{ color: 'var(--st-text-muted)' }}
        />
      </div>
      <span
        className="text-sm font-medium text-center px-4"
        style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
      >
        {label}
      </span>
    </div>
  );
};

export default ImagePlaceholder;
